import React, { useState, useEffect, useCallback } from 'react';
import { Settings, AlertTriangle, ToggleLeft, ToggleRight, Save, RefreshCw } from 'lucide-react';
import { apiService } from '../services/api';

interface SystemConfig {
  maintenanceMode: boolean;
  maintenanceBanner: string | null;
  defaultModel: string;
  featureFlags: Record<string, boolean>;
}

interface FlagRowProps {
  flagKey: string;
  value: boolean;
  onChange: (key: string, val: boolean) => void;
  description: string;
}

function FlagRow({ flagKey, value, onChange, description }: FlagRowProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-800 font-mono">{flagKey}</p>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
      <button
        onClick={() => onChange(flagKey, !value)}
        className="flex items-center gap-1.5 text-sm"
      >
        {value
          ? <><ToggleRight size={24} className="text-green-500" /><span className="text-green-600 font-medium">ON</span></>
          : <><ToggleLeft size={24} className="text-gray-300" /><span className="text-gray-400">OFF</span></>
        }
      </button>
    </div>
  );
}

const FLAG_DESCRIPTIONS: Record<string, string> = {
  memory_tier1: 'Tier 1 memory — markdown-on-R2 personal knowledge items',
  memory_tier2: 'Tier 2 memory — conversational insight opt-in (requires legal sign-off)',
  telemetry_enabled: 'Structured telemetry ingest (privacy toggle gating must be live first)',
  stripe_enabled: 'Stripe checkout + subscription lifecycle hooks',
};

export default function System() {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [draft, setDraft] = useState<SystemConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getSystemConfig();
      setConfig(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch {
      // Use safe defaults if config not yet seeded
      const defaults: SystemConfig = {
        maintenanceMode: false,
        maintenanceBanner: null,
        defaultModel: 'gpt-5.4-mini',
        featureFlags: {
          memory_tier1: false,
          memory_tier2: false,
          telemetry_enabled: false,
          stripe_enabled: false,
        },
      };
      setConfig(defaults);
      setDraft(JSON.parse(JSON.stringify(defaults)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const setFlag = (key: string, val: boolean) => {
    setDraft(d => d ? { ...d, featureFlags: { ...d.featureFlags, [key]: val } } : d);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await apiService.updateSystemConfig(draft);
      setConfig(JSON.parse(JSON.stringify(draft)));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const isDirty = JSON.stringify(config) !== JSON.stringify(draft);

  if (loading || !draft) {
    return <div className="p-10 text-center text-sm text-gray-400">Loading system config...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System</h1>
          <p className="mt-1 text-sm text-gray-500">Feature flags, maintenance mode, and global controls</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="text-gray-400 hover:text-gray-700">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={save}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-40"
          >
            <Save size={15} />
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className={`bg-white shadow rounded-lg overflow-hidden ${draft.maintenanceMode ? 'ring-2 ring-red-400' : ''}`}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <AlertTriangle size={16} className={draft.maintenanceMode ? 'text-red-500' : 'text-gray-400'} />
          <h2 className="font-semibold text-gray-900">Maintenance Mode</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Maintenance Mode Active</p>
              <p className="text-xs text-gray-400">CF Worker returns 503 to all non-admin requests</p>
            </div>
            <button onClick={() => setDraft(d => d ? { ...d, maintenanceMode: !d.maintenanceMode } : d)}>
              {draft.maintenanceMode
                ? <ToggleRight size={28} className="text-red-500" />
                : <ToggleLeft size={28} className="text-gray-300" />}
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Banner message (shown to users during maintenance)
            </label>
            <input
              type="text"
              value={draft.maintenanceBanner ?? ''}
              onChange={e => setDraft(d => d ? { ...d, maintenanceBanner: e.target.value || null } : d)}
              placeholder="We're performing scheduled maintenance. Back in ~30 minutes."
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Default Model */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Settings size={16} className="text-indigo-500" />
          <h2 className="font-semibold text-gray-900">Defaults</h2>
        </div>
        <div className="px-6 py-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">Default Model ID</label>
          <input
            type="text"
            value={draft.defaultModel}
            onChange={e => setDraft(d => d ? { ...d, defaultModel: e.target.value } : d)}
            className="w-72 border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-gray-400">Used when client does not specify a model. Must exist in model_configs.</p>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Feature Flags</h2>
          <p className="text-xs text-gray-400 mt-0.5">Globally gate features — changes take effect immediately on next CF Worker KV sync</p>
        </div>
        <div className="px-6">
          {Object.entries(draft.featureFlags).map(([key, val]) => (
            <FlagRow
              key={key}
              flagKey={key}
              value={val}
              onChange={setFlag}
              description={FLAG_DESCRIPTIONS[key] ?? 'No description'}
            />
          ))}
        </div>
      </div>

      {isDirty && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
          ⚠️ You have unsaved changes. Click "Save Changes" to apply.
        </div>
      )}
    </div>
  );
}
